/**
 * Notion風インラインセル編集コンポーネント
 *
 * テーブルセルをクリックするとその場で編集可能になる。
 * Enter/Blur で保存、Escape でキャンセル。
 */

import { Copy } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import type { TodoEnergy, TodoUpdate } from '@/api/types/todo'
import { parseUtcDate } from '@/lib/date-utils'
import { useTodoStore } from '@/store/useTodoStore'

function toLocalDatetime(iso: string): string {
  try {
    const d = parseUtcDate(iso)
    const jst = new Date(d.getTime() + 9 * 60 * 60 * 1000)
    return jst.toISOString().slice(0, 16)
  } catch {
    return ''
  }
}

type EditableCellField = 'title' | 'energy' | 'deadline' | 'scheduled_start' | 'estimated_minutes' | 'priority'

type InlineEditCellProps = {
  todoId: number
  field: EditableCellField
  value: string
  children: React.ReactNode
  className?: string
  inputClassName?: string
}

const ENERGY_OPTIONS: { value: TodoEnergy; label: string }[] = [
  { value: 'low', label: 'L' },
  { value: 'medium', label: 'M' },
  { value: 'high', label: 'H' },
]

export const InlineEditCell = ({
  todoId,
  field,
  value,
  children,
  className = '',
  inputClassName = '',
}: InlineEditCellProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement>(null)
  const patchTodo = useTodoStore((s) => s.patchTodo)

  useEffect(() => {
    // deadline/scheduled_start は表示用ISO → 編集用ローカルdatetime に変換して保持
    if (field === 'deadline' || field === 'scheduled_start') {
      setDraft(value ? toLocalDatetime(value) : '')
    } else {
      setDraft(value)
    }
  }, [value, field])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      if (inputRef.current instanceof HTMLInputElement) {
        inputRef.current.select()
      }
    }
  }, [isEditing])

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsEditing(true)
  }

  const save = () => {
    setIsEditing(false)
    // deadline/scheduled_start は変換済みdraftとvalueを比較（変換後同士で比較）
    const originalDraft =
      field === 'deadline' || field === 'scheduled_start' ? (value ? toLocalDatetime(value) : '') : value
    if (draft === originalDraft) return

    let payload: TodoUpdate = {}
    switch (field) {
      case 'title':
        if (!draft.trim()) return
        payload = { title: draft.trim() }
        break
      case 'energy':
        payload = { energy: draft as TodoEnergy }
        break
      case 'deadline':
        payload = { deadline: draft ? new Date(draft).toISOString() : null }
        break
      case 'scheduled_start':
        payload = { scheduled_start: draft ? new Date(draft).toISOString() : null }
        break
      case 'estimated_minutes':
        payload = { estimated_minutes: draft ? parseInt(draft, 10) : null }
        break
      case 'priority':
        payload = { priority: parseInt(draft, 10) || 50 }
        break
    }
    void patchTodo(todoId, payload)
  }

  const cancel = () => {
    // deadline/scheduled_start は変換後の値に戻す
    if (field === 'deadline' || field === 'scheduled_start') {
      setDraft(value ? toLocalDatetime(value) : '')
    } else {
      setDraft(value)
    }
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !e.nativeEvent.isComposing) {
      e.preventDefault()
      save()
    } else if (e.key === 'Escape') {
      cancel()
    }
  }

  if (!isEditing) {
    return (
      <td className={className} onClick={handleClick}>
        {children}
      </td>
    )
  }

  // Energy は select で表示
  if (field === 'energy') {
    return (
      <td className={className} onClick={(e) => e.stopPropagation()}>
        <select
          ref={inputRef as React.RefObject<HTMLSelectElement>}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value)
            // select は変更と同時に保存
            setIsEditing(false)
            if (e.target.value !== value) {
              void patchTodo(todoId, { energy: e.target.value as TodoEnergy })
            }
          }}
          onBlur={cancel}
          onKeyDown={handleKeyDown}
          className="w-full bg-background border border-primary/40 rounded text-[10px] px-0.5 py-px outline-none"
        >
          {ENERGY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </td>
    )
  }

  // Deadline / scheduled_start は datetime-local
  if (field === 'deadline' || field === 'scheduled_start') {
    return (
      <td className={className} onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type="datetime-local"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={handleKeyDown}
          className={`w-full bg-background border border-primary/40 rounded text-xs px-1 py-px outline-none ${inputClassName}`}
        />
      </td>
    )
  }

  // Estimated minutes / priority は number
  if (field === 'estimated_minutes' || field === 'priority') {
    return (
      <td className={className} onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type="number"
          min={0}
          max={field === 'priority' ? 100 : undefined}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={handleKeyDown}
          className={`w-full bg-background border border-primary/40 rounded text-xs px-1 py-px outline-none text-right ${inputClassName}`}
        />
      </td>
    )
  }

  // Title はテキスト入力
  return (
    <td className={className} onClick={(e) => e.stopPropagation()}>
      <input
        ref={inputRef as React.RefObject<HTMLInputElement>}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={handleKeyDown}
        className={`w-full bg-background border border-primary/40 rounded text-sm px-1 py-px outline-none ${inputClassName}`}
      />
    </td>
  )
}

/**
 * タイトル専用インライン編集
 * <td> 内の <span> を置き換えて使う（他の要素と共存可能）
 */
type InlineTitleEditProps = {
  todoId: number
  title: string
  className?: string
}

export const InlineTitleEdit = ({ todoId, title, className = '' }: InlineTitleEditProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(title)
  const inputRef = useRef<HTMLInputElement>(null)
  const patchTodo = useTodoStore((s) => s.patchTodo)

  useEffect(() => {
    setDraft(title)
  }, [title])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsEditing(true)
  }

  const save = () => {
    setIsEditing(false)
    if (!draft.trim() || draft.trim() === title) return
    void patchTodo(todoId, { title: draft.trim() })
  }

  const cancel = () => {
    setDraft(title)
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !e.nativeEvent.isComposing) {
      e.preventDefault()
      save()
    } else if (e.key === 'Escape') {
      cancel()
    }
  }

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
        className="flex-1 min-w-0 bg-background border border-primary/40 rounded text-sm px-1 py-px outline-none"
      />
    )
  }

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation()
    void navigator.clipboard.writeText(title)
    toast.success('コピーしました')
  }

  return (
    <span
      className={`${className} cursor-text hover:bg-accent/30 rounded px-0.5 -mx-0.5 inline-flex items-center gap-0.5`}
      onClick={handleClick}
    >
      {title}
      <button
        type="button"
        className="hidden group-hover/row:inline-flex shrink-0 p-0! text-muted-foreground hover:text-foreground transition-colors"
        onClick={handleCopy}
        aria-label="タイトルをコピー"
      >
        <Copy className="size-3" />
      </button>
    </span>
  )
}
